import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, ThemeProvider, Box } from '@material-ui/core';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';
import sleepioTheme from '@/themes/sleepioTheme';

// The Singular deep link Canopy intercepts to close the system browser and
// resume the session handoff. Hard-coded because the tab hop discards the
// original activation URL's redirect_to param — there is no other source.
const NATIVE_CALLBACK = 'sleepio://authCallback';

/**
 * Handles the magic link callback for Sleepio Canopy activation.
 * Route: /activate/sleepio-canopy/:lob/:org_slug/:intent/email/callback
 *
 * Intent is read from ActivationContext (initialised from the URL path by
 * ActivationRouter). Sign-up → activateSleepioConfirm then /coverage;
 * login → /complete directly.
 */
const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const base = useActivationBase();
  const redirectParam = buildRedirectParam(NATIVE_CALLBACK);

  useEffect(() => {
    const navigateToAuth = () => history.replace(`${base}/auth${redirectParam}`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!session) {
        logError('[SleepioEmailCallback]', 'No session after magic link');
        navigateToAuth();
        return;
      }

      dispatch({ type: 'SET_SESSION', session });

      if (state.accountIntent === AccountIntent.Create) {
        await activateSleepioConfirm({ accessToken: session.access_token });
        dispatch({ type: 'SET_STEP', step: 'coverage_form' });
        history.replace(`${base}/coverage${redirectParam}`);
      } else {
        dispatch({ type: 'SET_STEP', step: 'activation_complete' });
        history.replace(`${base}/complete${redirectParam}`);
      }
    };

    handleCallback().catch((err) => {
      logError('[SleepioEmailCallback]', 'Callback failed', err);
      navigateToAuth();
    });
  }, []); // eslint-disable-line

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export const EmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);
